import { slideUp, slideToggle } from './startup/slide';
import { validateJob } from './utils/validation';
import { startSpan as sentryStartSpan, captureException } from '@sentry/browser';
import { pushToDataLayer } from './utils/ga4-tracking/push-to-data-layer';
import { getCookie, setCookie } from './startup/cookies-helper';

const en = document.documentElement.lang === 'en';
//LIVE URL is set via DNS fomr on.ag
let recruitingURL = 'recruiting.ottonova.de';
if (window.location.host.indexOf('test.on.ag') > -1 || window.location.host.indexOf('localhost') > -1) {
  recruitingURL = 'recruiting.test.ottonova.on.ag';
} else if (window.location.host.indexOf('staging.on.ag') > -1) {
  recruitingURL = 'recruiting.staging.ottonova.on.ag';
}

if (document.location.href.indexOf('jobs?') > -1 && document.location.href.indexOf('?utm') === -1) {
  setTimeout(() => {
    const header = document.querySelector('#header');
    const headerHeight = header.offsetHeight;
    const entry = document.getElementById('jobList');
    if (entry) {
      const adder = parseInt(getComputedStyle(entry).getPropertyValue('margin-top').replace('px', ''));
      window.scrollTo({
        top: entry.getBoundingClientRect().top - document.body.getBoundingClientRect().top - headerHeight - adder,
        behavior: 'smooth'
      });
    }
  }, 50);
}

const startSpan = (name, op, attributes, cb) => (sentryStartSpan ? sentryStartSpan({ name, op, attributes }, cb) : cb());

const setContent = (block, object) => {
  const load = block.querySelector('.load');

  load.innerHTML = '';

  const h3 = document.createElement('h3');
  h3.textContent = object.title;

  block.querySelector('.grid > nav > div > span').textContent = object.title;

  document.title = object.title + ' - ottonova Jobs';

  document.querySelectorAll('[property="og:description"], [name="description"]').forEach(elem => {
    let contentValue;
    if (object.title == 'Initiativ bewerben (m/w)') {
      contentValue = en ? 'Join the revolution! Apply now!' : 'Schließe Dich der Revolution an! Jetzt initiativ bewerben!';
    } else {
      contentValue = en ? 'Join the revolution! We are looking for a ' + object.title + '. Apply now!' : 'Schließe Dich der Revolution an! Wir suchen eine/n ' + object.title + '. Bewirb Dich jetzt!';
    }
    elem.setAttribute('content', contentValue);
  });

  if (document.querySelector('[property="og:url"]')) {
    document.querySelector('[property="og:url"]').setAttribute('content', 'https://www.ottonova.de/unternehmen/jobs/' + object.url);
  }

  load.appendChild(h3);

  for (const [, value] of Object.entries(object.description)) {
    const h5 = document.createElement('h5');
    h5.textContent = value.title;

    const text = document.createElement('div');
    text.innerHTML = value.text;

    //sanitize the html
    text.querySelectorAll('[style]').forEach(tag => tag.removeAttribute('style'));

    load.appendChild(h5);
    load.appendChild(text);
  }

  block.querySelector('#job_position_id').value = object.id;
};

const jobListBlocks = document.querySelectorAll('.jobList-block');

if (jobListBlocks.length) {
  // print button
  jobListBlocks.forEach(block => {
    block.querySelector('.printer').addEventListener('click', ev => {
      ev.preventDefault();
      window.print();
    });
  });

  const fallbackCategory = en ? 'Other' : 'Sonstige';

  //fetch and parse the job -data
  const fetchJobData = english =>
    startSpan('jobs.fetch', 'function', { language: english ? 'en' : 'de' }, () =>
      fetch(english ? 'https://ottonova.jobs.personio.de/xml?language=en' : 'https://ottonova.jobs.personio.de/xml')
        .then(response => response.text())
        .then(response => new DOMParser().parseFromString(response, 'text/xml'))
        .then(data => {
          const newData = {};

          data.querySelectorAll('position').forEach(position => {
            const category = position.querySelector('recruitingCategory')?.textContent || fallbackCategory;
            const id = position.querySelector('id').textContent;
            if (!(category in newData)) {
              newData[category] = {};
            }
            newData[category][id] = position;
          });
          return newData;
        })
    ).catch(error => {
      captureException(error, { extra: { scope: 'jobs.fetch' } });
      throw error;
    });

  const searchParams = new URLSearchParams(window.location.search);
  const jobIdentifier = searchParams.toString();
  const host = window.location.protocol + '//' + window.location.hostname;

  jobListBlocks.forEach(async block => {
    const allJobsData = {};
    const jobData = await fetchJobData(en);
    const sortedData = Object.entries(jobData).sort((a, b) => {
      if (a[0] === fallbackCategory) return 1;
      if (b[0] === fallbackCategory) return -1;
      return 0;
    });

    sortedData.forEach(([category, jobs]) => {
      for (const [id, object] of Object.entries(jobs)) {
        const title = object.querySelector('name').textContent;
        const description = [];
        let url = null;
        let jobDescriptions = object.querySelectorAll(':scope > jobDescriptions jobDescription');
        if (!jobDescriptions.length) {
          continue;
        }
        jobDescriptions.forEach(jobDesc => {
          if (jobDesc.querySelector('name').textContent == 'URL') {
            url = jobDesc
              .querySelector('value')
              .textContent.replace('<![CDATA[', '')
              .replace(']]>', '')
              .replace(/<[^>]*>?/gm, '')
              .replace(/\u200b/g, '')
              .trim();
          } else {
            let elem = {};
            elem.title = jobDesc.querySelector('name').textContent;
            elem.text = jobDesc.querySelector('value').textContent.replace('<![CDATA[', '').replace(']]>', '').trim();
            description.push(elem);
          }
        });
        if (!allJobsData[category]) {
          allJobsData[category] = [];
        }
        allJobsData[category].push({
          url: url,
          id,
          title,
          url,
          description
        });
      }
    });

    if (Object.keys(allJobsData).length === 0) {
      block.querySelector('.container').innerHTML = `
        <div class="h2">${en ? 'No job openings available' : 'Keine Stellenangebote verfügbar'}</div>
        <p>${en ? "We're sorry, but there are currently no jobs available in English. Are you fluent in German? If so, check out our German job listings." : 'Es tut uns leid, aber es gibt derzeit keine Stellenangebote auf Deutsch. Du sprichst fließend Englisch? Wenn ja, schau dir unsere englischen Stellenangebote an.'}</p>
        <a class="btn" href="${en ? '/unternehmen/jobs' : '/en/company/jobs'}" style="display: block; width: 250px; margin: auto;">${en ? 'View german jobs' : 'Englische Stellen ansehen'}</a>
      `;
      return;
    }

    Object.keys(allJobsData).forEach(category => {
      let categoryAdded = false;
      const categoryItem = document.createElement('li');
      const categoryLabel = en && category === 'Werkstudenten / Praktikanten' ? 'working students / interns' : category;
      categoryItem.innerHTML = `<span class="caption">${categoryLabel}</span>`;

      for (const [id, object] of Object.entries(allJobsData[category])) {
        if (!categoryAdded) {
          categoryAdded = true;
          if (!categoryLabel.includes('nitiativ')) {
            //HIDE INITIAVE APPLICATION CATEGORY because it is shown first anyway and has only one job
            block.querySelector('.grid > nav > ul').appendChild(categoryItem);
          }
        }
        const link = document.createElement('a');
        link.dataset.url = object.url;
        link.setAttribute('data-tr-event', 'application_interest');
        link.setAttribute('data-tr-element_section', 'jobList-block');
        link.setAttribute('data-tr-link_text', object.title);
        link.setAttribute('data-tr-link_url', `${host}/${en ? 'en/company/' : 'unternehmen/'}jobs?${object.url}`);
        link.setAttribute('data-tr-application_position', object.title);
        link.textContent = object.title;
        const listEntry = document.createElement('li');
        listEntry.appendChild(link);

        link.addEventListener('click', e => {
          e.preventDefault();
          if (link.parentElement.classList.contains('active')) {
            return;
          }
          changeActiveItem(link);
          history.pushState({}, '', '?' + link.dataset.url);
          if (!window['google_tag_manager']) {
            return;
          }

          const ga4Event = {};
          for (const attribute of link.attributes) {
            if (attribute.name.startsWith('data-tr-')) {
              ga4Event[attribute.name.replace('data-tr-', '')] = attribute.value;
            }
          }
          pushToDataLayer(ga4Event);
        });
        if (id === '79391') {
          block.querySelector('.grid > nav > ul').insertBefore(listEntry, block.querySelector('.grid > nav > ul').firstChild);
        } else {
          block.querySelector('.grid > nav > ul').appendChild(listEntry);
        }
      }
    });

    const links = block.querySelectorAll('.grid > nav > ul li > a');

    const initActiveJob = Object.values(allJobsData)
      .flat()
      .find(job => jobIdentifier.includes(job.url));

    changeActiveItem(initActiveJob ? Array.from(links).find(link => link.dataset.url === initActiveJob.url) : links[0]);

    function changeActiveItem(activeLink) {
      links.forEach(link => link.parentElement.classList.toggle('active', activeLink === link));
      const activeJobObject = Object.values(allJobsData)
        .flat()
        .find(job => job.url === activeLink.dataset.url);
      setContent(block, activeJobObject);
    }

    // NAVIGATION
    block.querySelector('.grid > nav > div').addEventListener('click', e => {
      block.querySelector('.grid > nav > div').classList.toggle('active');
      slideToggle(block.querySelector('.grid > nav > ul'));
    });

    // FILE HANDLING
    const output = [];
    const allFiles = [];
    let indexes = 0;
    const form = block.querySelector('form');
    const button = form.querySelector('.submitter');
    const uploadButton = form.querySelector('a.upload');

    function handleFileSelect(evt, output) {
      let files = evt.target.files;
      let error = false;
      for (let i = 0; i < files.length; i++) {
        //check file exentsion and set error bool, if wrong type
        let regexFileExtension = /(?:\.([^.]+))?$/;
        let ext = regexFileExtension.exec(files[i].name)[1].trim().toLowerCase();
        if (['pdf', 'docx', 'doc', 'jpg', 'jpeg', 'png', 'txt', 'odt', 'ods', 'rtf'].includes(ext)) {
          output[indexes] =
            '<div class="file"><i data-index="' + indexes + '" tabindex="0"></i><span>' + encodeURI(ext) + '</span><strong>' + encodeURI(files[i].name).replace('%20', ' ') + '</strong></div>';
        } else {
          output[indexes] =
            '<div class="file error fileerror"><i data-index="' +
            indexes +
            '" tabindex="0"></i><span>' +
            encodeURI(ext) +
            '</span><strong>' +
            encodeURI(files[i].name).replace('%20', ' ') +
            '</strong></div>';
          error = true;
        }
        allFiles[indexes] = files[i];
        if (allFiles.filter(() => true).length > 5) {
          output[indexes] = '<div class="file error"><i data-index="' + indexes + '"></i><span>' + encodeURI(ext) + '</span><strong>' + (en ? 'not valid' : 'nicht möglich') + '</strong></div>';
          error = true;
        }
        indexes++;
      }
      block.querySelector('.dropFiles').dataset.error = error;
      if (allFiles.filter(() => true).length > 5) {
        block.querySelector('.dropFiles').classList.remove('active');
        uploadButton.style.display = 'none';
        error = true;
      } else if (allFiles.filter(() => true) == 5 && !error) {
        block.querySelector('.dropFiles').classList.add('active');
        uploadButton.style.display = 'none';
      } else {
        block.querySelector('.dropFiles').classList.add('active');
        uploadButton.style.display = 'inherit';
      }
      if (error) {
        block.querySelector('.dropFiles').classList.add('error');
        button.setAttribute('disabled', 'disabled');
      } else {
        button.removeAttribute('disabled');
      }
      block.querySelector('.dropFiles').classList.remove('over');
      document.getElementById('list').innerHTML = output.join('');
    }

    document.getElementById('documents').addEventListener(
      'change',
      function (e) {
        handleFileSelect(e, output);
        closeHandler();
      },
      false
    );

    function deleteFile(link) {
      const index = link.dataset.index;
      output.splice(index, 1);
      allFiles.splice(index, 1);
      document.getElementById('list').innerHTML = output.join('');
      if (block.querySelector('.dropFiles .file') == null) {
        block.querySelector('.dropFiles').classList.remove('active');
        block.querySelector('.dropFiles').classList.remove('over');
        button.setAttribute('disabled', 'disabled');
      }
      if (block.querySelector('.dropFiles .file.error') == null) {
        block.querySelector('.dropFiles').classList.remove('error');
        if (allFiles.length) {
          block.querySelector('.dropFiles').classList.add('active');
          button.removeAttribute('disabled');
        }
      }
      closeHandler();
    }

    function closeHandler() {
      block.querySelectorAll('.dropFiles .file i').forEach(link => {
        link.addEventListener('click', e => {
          deleteFile(link);
        });
        link.addEventListener('keydown', e => {
          if (e.key === 'Enter') {
            deleteFile(link);
          }
        });
      });
    }

    // HANDLING DRAG & DROP
    function handleDragEnter(e) {
      this.classList.add('over');
    }
    function handleDragLeave(e) {
      this.classList.remove('over');
    }
    block.querySelector('.dropFiles').addEventListener('dragenter', handleDragEnter, false);
    block.querySelector('.dropFiles').addEventListener('dragleave', handleDragLeave, false);

    //DROPDOWN
    block.querySelector('form select').addEventListener('change', e => {
      let misc = block.querySelector('select.from').value === 'Sonstige' ? true : false;
      block.querySelector('.formular .from').setAttribute('name', misc ? '' : 'custom_attribute_122851');
      const otherInput = block.querySelector('.formular .other input');
      otherInput.setAttribute('name', misc ? 'custom_attribute_122851' : '');
      otherInput.setAttribute('tabindex', misc ? '0' : '-1');
      otherInput.required = misc ? true : false;
      block.querySelector('.formular .other').classList.toggle('active', misc);
    });

    button.addEventListener('click', e => {
      block.querySelector('.dropFiles').classList.remove('shake');
      if (block.querySelector('.dropFiles').classList.contains('error')) {
        block.querySelector('.dropFiles').classList.add('shake');
        return;
      }
      if (validateJob.validateAll(form).length || form.classList.contains('submitted')) {
        return;
      }
      let formData = new FormData(form);
      formData.delete('documents[]');
      for (let prop in allFiles) {
        if (allFiles.hasOwnProperty(prop)) {
          formData.append('documents', allFiles[prop]);
        }
      }
      button.classList.add('load');
      //STRIP SPACES FROM PHONE NUMBER
      const phone = formData.get('phone');
      formData.set('phone', phone.replace(/\s/g, ''));

      startSpan('jobs.submit', 'http.client', { recruitingURL, fileCount: allFiles.length }, () =>
        fetch(`https://${recruitingURL}/application`, {
          method: 'POST',
          body: formData
        })
      )
        .catch(error => {
          captureException(error, { extra: { recruitingURL, fileCount: allFiles.length } });
          slideUp(
            form,
            () => {
              form.style.display = 'none';
            },
            300
          );
          block.querySelector('.formular .top p').classList.add('red');
          block.querySelector('.formular .top p').textContent = en
            ? 'The connection to the server could not be established. Our systems are currently offline or the uploaded file is too large. Please check if your files are smaller than 20 MB. To try again, reload this page.'
            : 'Die Verbindung zum Server konnte nicht hergestellt werden. Unsere Systeme sind gerade offline oder die hochgeladene Datei ist zu groß. Bitte überprüfe, ob Deine Dateien kleiner als 20 MB sind. Um es nochmals zu versuchen, lade diese Seite neu.';
          form.querySelector('button').classList.remove('load');
          console.error('There was a problem with the Fetch operation: ', error.status);
          return false;
        })
        .then(response => response.json())
        .then(data => {
          //GA4 Tracking for submitting job
          const application_source_value = form.querySelector('select.form-select');
          const ga4Event = {
            event: 'application_sent',
            element_section: 'jobList-block',
            link_text: document.querySelector('.job>.load>h3').innerText,
            application_position: document.querySelector('.job>.load>h3').innerText,
            application_source: application_source_value.options[application_source_value.selectedIndex].value
          };
          pushToDataLayer(ga4Event);
          slideUp(
            form,
            () => {
              form.style.display = 'none';
            },
            300
          );
          if (data.error) {
            block.querySelector('.formular .top p').classList.add('red');
            document.querySelector('body').classList.add('shake');
            let errorMessage = '';
            switch (data.error.status) {
              case 422:
                errorMessage = en ? data.error.message : 'Bitte mindestens einen Lebenslauf hochladen.';
                break;
              case 413:
                errorMessage = en ? data.error.message : 'Hochgeladene Datei ist zu groß.';
                break;
              case 500:
                errorMessage = en ? data.error.message : 'Server nicht erreichbar.';
                break;
              case 415:
                errorMessage = en ? data.error.message : 'Dateiformat wird nicht unterstützt.';
                break;
              case 409:
                errorMessage = en ? data.error.message : 'Jemand mit dieser E-mail-adresse hat sich bereits beworben.';
                break;
              default:
                errorMessage = en ? 'server timeout or no server found' : 'Server nicht erreichbar';
            }
            block.querySelector('.formular .top p').textContent = en
              ? 'Oh no! An error has occurred. Please reload the page and try again. (status: ' + errorMessage + ')'
              : 'Oh nein! Ein Fehler ist aufgetreten. Lade die Seite bitte erneut und versuche es noch einmal. (Grund: ' + errorMessage + ')';
            form.querySelector('button').classList.remove('load');
          } else {
            block.querySelector('.formular .top p').classList.add('green');
            block.querySelector('.formular .top p').textContent = en
              ? "Thank you for your application! We have sent you a confirmation by email. We'll get back to you with the next steps."
              : 'Danke für Deine Bewerbung! Eine Bestätigung haben wir dir per E-mail geschickt. Wir melden uns mit den nächsten Schritten bei dir.';
            form.querySelector('button').classList.remove('load');
            form.classList.add('submitted');

            let imgPixel = document.createElement('img');
            imgPixel.src = '//conv.indeed.com/pagead/conv/1918429238657757/?script=0';
            imgPixel.height = 1;
            imgPixel.width = 1;
            document.querySelector('body').appendChild(imgPixel);
          }
        });
    });
  });
}
